import React, { useCallback, useEffect, useState } from 'react';
import { formatEpoch, toTitleCase } from '../../utils/helper';
import { getAxios, postAxios } from '../../axios/Axios';
import request from '../../request/request';
import Select from 'react-select';
import { useLocation, useNavigate } from 'react-router-dom';
import Loader from '../loader/Loader';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import {ToastTypes} from '../constants/Constants';

const GrantProductSubscription = ({ changeToast }) => {
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();
	const party_id = location.state || '';
	const [products, setProducts] = useState([]);
	const [data, setData] = useState({});
	const [selectedProduct, setSelectedProduct] = useState(null);
	const [errors, setErrors] = useState({});
	const [formData, setFormData] = useState({
		root_category_name: '',
		catalog_name: '',
		website_host: '',
		web_site_id: '',
		website_name: '',
		catalog_id: '',
		root_category_id: '',
		webapp_path: '',
	});

	const [catlogerrors, setCatlogErrors] = useState({});
	const [prefilledFields, setPrefilledFields] = useState(new Set());

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			const partyData = await getAxios(`${request.getAllParty}`, {
				params: { party_id },
			});

			if (partyData?.data?.apiresponse?.type === 'OK') {
				const partyRecord = partyData?.data?.records[0];
				setData(partyRecord);
				
				// Prefill form data with existing values
				const newFormData = {
					root_category_name: partyRecord?.root_cat_name || '',
					catalog_name: partyRecord?.catalog_name || '',
					website_host: partyRecord?.website_host || '',
					web_site_id: partyRecord?.website_id || '',
					website_name: partyRecord?.website_name || '',
					catalog_id: partyRecord?.catalog_id || '',
					root_category_id: partyRecord?.root_cat_id || '',
					webapp_path: partyRecord?.webapp_path || '',
				};
				
				setFormData(newFormData);
				
				// Track which fields were prefilled (had original values)
				const prefilled = new Set();
				Object.entries(newFormData).forEach(([key, value]) => {
					if (value && value.trim() !== '') {
						prefilled.add(key);
					}
				});
				setPrefilledFields(prefilled);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
		}
	}, [party_id]);

	const fetchProducts = useCallback(async () => {
		try {
			setLoading(true);
			const res = await getAxios(
				`${request?.getBmbSubscriptionProducts}?rows=500&page=1`
			);
			if (res?.data?.apiresponse?.type === 'OK') {
				setProducts(
					res?.data?.records?.map((item) => ({
						label: item?.product_name,
						value: item?.product_id,
					}))
				);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (party_id) {
			fetchData();
			fetchProducts();
		}
	}, [fetchData, fetchProducts, party_id]);

	const grantSubscription = useCallback(async () => {
		try {
			if (!selectedProduct) {
				setErrors({ positionPortalError: 'Please select a product.' });
				return;
			}
			setErrors({});

			const payload = {
				party_id,
				product_id: selectedProduct,
			};

			const res = await postAxios(request.grantProductSubscription, payload);
			if (res?.data?.apiresponse?.type === 'OK') {
				changeToast('success', res?.data?.apiresponse?.message);
				fetchData();
			} else {
				changeToast(ToastTypes.TOAST_ERROR, res?.data?.apiresponse?.message);
			}
		} catch (error) {
			console.log(error);
			changeToast('error', 'Failed to grant subscription');
		}
	}, [selectedProduct, party_id, changeToast, fetchData]);

	const partyDetails = [
		{
			label: 'PARTY NAME',
			name: `${toTitleCase(data?.party_name)}`,
			ID: data?.party_id,
		},
		{
			label: 'ADDRESS',
			name: data?.address,
			ID: data?.contact_number,
		},
		{
			label: 'BIO',
			name: data?.bio,
		},
	];

	const handleChange = (e) => {
		const { name, value } = e.target;
		let newValue = value;

		if (name.endsWith('_id')) {
			const specialCharRegex = /[^A-Za-z0-9_]/;
			let error = '';

			if (specialCharRegex.test(value)) {
				error = 'No Special Character are allowed.';
			} else if (value.length > 20) {
				error = 'Maximum 20 characters allowed.';
			}

			newValue = value
				.replace(/[^A-Za-z0-9_]/g, '')
				.toUpperCase()
				.slice(0, 20);

			setCatlogErrors((prev) => ({
				...prev,
				[name]: error,
			}));
		} else {
			setCatlogErrors((prev) => ({ ...prev, [name]: '' }));
		}

		setFormData((prev) => ({ ...prev, [name]: newValue }));
	};

	const validate = () => {
		const newErrors = {};
		Object.entries(formData).forEach(([key, value]) => {
			if (key === 'webapp_path') return;
			
			if (!value && key !== 'party_id') {
				newErrors[key] = 'This field is required';
			}
		});
		setCatlogErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleWebsiteCatlog = async () => {
		if (!validate()) return;

		try {
			const payload = {
				...formData,
				party_id: data?.party_id || '',
			};

			const res = await postAxios(request.websiteCatlog, payload);
			if (res?.data?.apiresponse?.type === 'OK') {
				changeToast('success', res?.data?.apiresponse?.message);
				fetchData();
			} else {
				changeToast(ToastTypes.TOAST_ERROR, res?.data?.apiresponse?.message);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const inputStyle = {
		height: '32px', // smaller height
		fontSize: '14px',
	};
	
	// Helper function to check if a field was prefilled from data
	const wasPrefilled = (fieldName) => {
		return prefilledFields.has(fieldName);
	};
	
	const renderInput = (label, name, disabled = false, required = true) => (
		<div className="form-group mb-3 w-50 px-2">
			<label className="form-label">
				{label}
				{required && <span style={{ color: 'red' }}> *</span>}
			</label>
			<InputText
				name={name}
				value={formData[name]}
				onChange={handleChange}
				className={`w-100 ${catlogerrors[name] ? 'p-invalid' : ''}`}
				style={inputStyle}
				disabled={disabled || wasPrefilled(name)}
			/>
			{catlogerrors[name] && (
				<small className="p-error">{catlogerrors[name]}</small>
			)}
		</div>
	);


	return (
		<div className="w-100 d-flex flex-row gap-5">
			{loading || !data?.party_id ? (
				<Loader />
			) : (
				<>
					<div className="update-category-details-container d-flex flex-column gap-0 p-4">
						{partyDetails?.map((detail, index) => (
							<div
								key={index}
								className="update-category-detail d-flex flex-column"
							>
								<span>{detail?.label}</span>
								<span>{detail?.name}</span>
								{detail?.ID && (
									<span>
										<strong>ID:</strong> {detail?.ID}
									</span>
								)}
							</div>
						))}
						<span className="icon-class">PROFILE PIC :</span>
						<div className="mb-2 gap-2 banner-category">
							<div className="position-relative banner-category-tileBanner">
								<img
									src={data?.profile_pic || '/images/tile-no-image.webp'}
									alt="Banner Preview"
									style={{ width: '100%' }}
									className="p-1 rounded"
								/>
							</div>
						</div>
					</div>

					<div className="d-flex flex-column w-100 gap-2">
						<div className="card update-category-form d-flex align-items-center align-self-start">
							<div className="w-100 d-flex justify-content-center pt-0 p-4 m-auto">
								<div className="card-body p-0">
									{data?.product_subscriptions?.length > 0 && (
										<>
											<div className="fw-bold mb-2">Existing Subscriptions</div>
											<DataTable
												value={data?.product_subscriptions}
												showGridlines
											>
												<Column field="subs_desc" header="Description" />
												<Column field="product_id" header="Product ID" />
												<Column
													field="from_date"
													header="From Date"
													body={(rowData) =>
														formatEpoch(rowData.from_date * 1000)
													}
												/>
												<Column
													field="thru_date"
													header="To Date"
													body={(rowData) =>
														formatEpoch(rowData.thru_date * 1000)
													}
												/>
												<Column
													field="subscription_duration_in_moths"
													header="Duration (Months)"
												/>
												<Column
													field="grace_period_on_expiry_in_days"
													header="Grace Period (Days)"
												/>
											</DataTable>
										</>
									)}

									<div className="fw-bold mb-2 mt-4">
										Grant a Product Subscription
									</div>
									<div className="form-group mb-3">
										<label className="form-label form-required">
											Select Product
										</label>
										<Select
											options={products}
											className="basic-multi-select"
											classNamePrefix="select"
											placeholder="Select product"
											onChange={(e) => setSelectedProduct(e.value)}
										/>
										{errors.positionPortalError && (
											<span style={{ color: 'red', fontSize: '12px' }}>
												{errors.positionPortalError}
											</span>
										)}
									</div>

									<div className="d-flex prime-react-btn w-100 align-items-center gap-2">
										<button
											className="btn w-100 text-white"
											style={{ backgroundColor: 'grey' }}
											onClick={() => navigate('/party-listing')}
										>
											Go Back
										</button>
										<button
											type="submit"
											className="btn submit-btn w-100 text-white"
											onClick={grantSubscription}
										>
											{loading ? 'Granting...' : 'Grant Subscription'}
										</button>
									</div>
								</div>
							</div>
						</div>

						<div className="card update-category-form d-flex align-items-center align-self-start">
							{
								//only when the party_type_id is only PARTY_GROUP then below section will be shown
								data?.party_type_id === 'PARTY_GROUP' && (
									<div className="w-100 d-flex justify-content-center pt-0 p-4 m-auto">
										<div className="card-body p-0" style={{ maxWidth: '900px' }}>
											<div className="fw-bold mb-3 mt-4 fs-5">
												Add Website Catalog
											</div>

											{/* Two inputs per row */}
											<div className="d-flex flex-wrap">
												{renderInput('Catalog ID', 'catalog_id')}
												{renderInput('Catalog Name', 'catalog_name')}
												{renderInput('Root Category ID', 'root_category_id')}
												{renderInput('Root Category Name', 'root_category_name')}
												{renderInput('Web Site ID', 'web_site_id')}
												{renderInput('Website Name', 'website_name')}
												{renderInput('Website Host', 'website_host')}
												{renderInput('Webapp Path', 'webapp_path', false, false)}
											</div>

											<div className="d-flex prime-react-btn w-100 align-items-center gap-2 mt-3">
												<button
													className="btn w-100 text-white"
													style={{ backgroundColor: 'grey' }}
													onClick={() => navigate('/party-listing')}
												>
													Go Back
												</button>
												<button
													type="submit"
													className="btn submit-btn w-100 text-white"
													onClick={handleWebsiteCatlog}
												>
													{loading ? 'Adding...' : 'Add Website Catalog'}
												</button>
											</div>
										</div>
									</div>
								)}
						</div>
					</div>
				</>
			)}
		</div>
	);
};

export default GrantProductSubscription;
